import React, { useState, useEffect } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, gql } from '@apollo/client'
import { CREATE_PRODUCT } from '../productMutations'
import { useNavigate } from 'react-router-dom'
import { ProductTypeCard } from './ProductTypeCard'
import { SimpleProductIcon, ConfigurableProductIcon } from './ProductIcons'
import { FurnitureTypeCard } from './FurnitureTypeCard'
import {
  SofaIcon,
  ChairIcon,
  BedIcon,
  TableIcon,
  LightsIcon,
  FabricsIcon,
  OtherIcon,
} from './FurnitureTypeIcons'
import ManufacturerSelect from '@/components/shared/inputComponents/ManufacturerSelect'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'

// UI Components
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'

// Define the form schema for validation
const formSchema = z.object({
  productType: z.enum(['SIMPLE_PRODUCT', 'ADVANCED_PRODUCT']),
  furnitureType: z
    .enum([
      'SOFA',
      'BED',
      'CHAIR_ARMCHAIR',
      'TABLE',
      'OTHER_WITH_FABRICS',
      'OTHER',
      'LIGHTS',
    ])
    .optional(),
  manufacturer: z.number().min(1, 'Manufacturer is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ProductCreationWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ProductCreationWizard: React.FC<ProductCreationWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  // Setup navigation
  const navigate = useNavigate()

  // Wizard state
  const [step, setStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      productType: 'ADVANCED_PRODUCT',
      furnitureType: undefined,
      manufacturer: undefined,
      name: '',
      description: '',
    },
  })

  // Reset wizard state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset to first step when the dialog opens
      setStep(1)
      form.reset({
        productType: 'ADVANCED_PRODUCT',
        furnitureType: undefined,
        manufacturer: undefined,
        name: '',
        description: '',
      })
    }
  }, [isOpen, form])

  // Setup mutation
  const [createProduct, { error }] = useMutation(CREATE_PRODUCT, {
    onCompleted: data => {
      form.reset()
      setStep(1)
      setIsSubmitting(false)

      // Get the ID of the newly created product
      const productId = data.createNewAdvancedProduct.id

      // Call the success callback if provided
      if (onSuccess) onSuccess()

      // Close the modal
      onClose()

      // Navigate to the product details page
      navigate(`/products/${productId}`)
    },
    onError: () => {
      setIsSubmitting(false)
    },
  })

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    setIsSubmitting(true)

    // For advanced product (configurable product)
    if (values.productType === 'ADVANCED_PRODUCT') {
      const variables = {
        advanced_product_profiles: [
          {
            id: null,
            name: values.name,
            long_product_name: '',
            original_product_name: '',
            description: values.description || '',
            language: 'en', // Default language
            meta_information: {
              id: null,
              meta_title: null,
              meta_description: null,
              permalink: '',
            },
          },
        ],
        visible: true,
        featured: false,
        manufacturer: values.manufacturer,
        categories: [],
        advanced_product_skeleton: {
          advanced_product_type: values.furnitureType || 'SOFA',
          manufacturer: values.manufacturer,
          sofa_shapes: [],
        },
        price_from: 0, // Set a default price of 0
      }

      createProduct({
        variables: variables,
      })
    }
  }

  // Navigation handlers
  const handleNext = () => {
    if (step === 1) {
      const productType = form.getValues('productType')
      if (productType) {
        setStep(2)
      }
    } else if (step === 2) {
      const furnitureType = form.getValues('furnitureType')
      if (furnitureType) {
        setStep(3)
      }
    } else if (step === 3) {
      const manufacturer = form.getValues('manufacturer')
      if (manufacturer) {
        setStep(4)
      }
    } else if (step === 4) {
      form.handleSubmit(onSubmit)()
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  // Determine if next button should be enabled
  const isNextDisabled = () => {
    if (step === 1) {
      return false // Always enabled for first step
    } else if (step === 2) {
      return false // Moving to next step is handled by card click
    } else if (step === 3) {
      return !form.getValues('manufacturer')
    } else if (step === 4) {
      return !form.formState.isValid
    }
    return false
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Create New Product</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {step === 1 && (
              <div>
                <h2 className="text-lg font-medium mb-4">
                  Select Product Type
                </h2>
                <div className="grid grid-cols-2 gap-6">
                  <ProductTypeCard
                    title="Simple Product"
                    description="A product with a unique SKU that can be tracked in inventory. Ideal for ready-made items with stock management needs."
                    icon={<SimpleProductIcon />}
                    onSelect={() => {
                      form.setValue('productType', 'SIMPLE_PRODUCT')
                      // We don't advance to the next step since this type is disabled
                    }}
                    disabled={true}
                  />
                  <ProductTypeCard
                    title="Configurable Product"
                    description="Custom-made products that can't be tracked in inventory. Perfect for furniture and other items that are made-to-order with multiple configuration options."
                    icon={<ConfigurableProductIcon />}
                    onSelect={() => {
                      form.setValue('productType', 'ADVANCED_PRODUCT')
                      setStep(2)
                    }}
                  />
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="text-lg font-medium mb-4">
                  Select Furniture Type
                </h2>
                <div className="grid grid-cols-3 gap-4">
                  <FurnitureTypeCard
                    title="Sofa"
                    icon={<SofaIcon />}
                    onSelect={() => {
                      form.setValue('furnitureType', 'SOFA')
                      setStep(3)
                    }}
                  />
                  <FurnitureTypeCard
                    title="Chair / Armchair"
                    icon={<ChairIcon />}
                    onSelect={() => {
                      form.setValue('furnitureType', 'CHAIR_ARMCHAIR')
                      setStep(3)
                    }}
                  />
                  <FurnitureTypeCard
                    title="Bed"
                    icon={<BedIcon />}
                    onSelect={() => {
                      form.setValue('furnitureType', 'BED')
                      setStep(3)
                    }}
                  />
                  <FurnitureTypeCard
                    title="Table / Coffe Table"
                    icon={<TableIcon />}
                    onSelect={() => {
                      form.setValue('furnitureType', 'TABLE')
                      setStep(3)
                    }}
                  />
                  <FurnitureTypeCard
                    title="Lights"
                    icon={<LightsIcon />}
                    onSelect={() => {
                      form.setValue('furnitureType', 'LIGHTS')
                      setStep(3)
                    }}
                  />
                  <FurnitureTypeCard
                    title="Other with fabrics"
                    icon={<FabricsIcon />}
                    onSelect={() => {
                      form.setValue('furnitureType', 'OTHER_WITH_FABRICS')
                      setStep(3)
                    }}
                  />
                  <FurnitureTypeCard
                    title="Other"
                    icon={<OtherIcon />}
                    onSelect={() => {
                      form.setValue('furnitureType', 'OTHER')
                      setStep(3)
                    }}
                  />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-lg font-medium mb-4">
                  Select Manufacturer
                </h2>
                <FormField
                  control={form.control}
                  name="manufacturer"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ManufacturerSelect field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-blue-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Important Information
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          The manufacturer you select will determine what
                          fabrics and additional components will be available
                          for this product. Make sure to choose the correct
                          manufacturer as this affects the product configuration
                          options.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Product name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Product description"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {error && (
              <div className="text-destructive text-sm">
                Error: {error.message}
              </div>
            )}
          </form>
        </Form>

        <DialogFooter className="flex justify-between">
          {step === 1 ? (
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          ) : step === 2 ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={isSubmitting}
            >
              Back
            </Button>
          ) : step === 3 ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <div className="flex-grow"></div>
              <Button
                type="button"
                onClick={handleNext}
                disabled={isNextDisabled() || isSubmitting}
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <div className="flex-grow"></div>
              <Button
                type="button"
                onClick={() => form.handleSubmit(onSubmit)()}
                disabled={!form.formState.isValid || isSubmitting}
              >
                Create Product
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ProductCreationWizard
